import { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabase';
import { API_BASE_URL } from '../../utils/apiConfig';
import toast from 'react-hot-toast';
import LoadingSpinner from '../ui/LoadingSpinner';
import EmptyState from '../ui/EmptyState';

interface LeaveRecord {
  id: string;
  teamMember: {
    id: string;
    name: string;
    displayName?: string;
    email: string;
    phoneNo?: string;
    role: string;
  };
  gym?: {
    id: string;
    name: string;
    displayName: string;
  };
  leaveType: string;
  startDate: string;
  endDate: string;
  isHalfDay: boolean;
  halfDayType?: string;
  reason?: string;
  status: 'pending' | 'approved' | 'rejected' | 'deleted';
  approvedBy?: {
    id: string;
    name: string;
  };
  approvedAt?: string;
  rejectionReason?: string;
  createdAt: string;
}

interface TeamLeavesTabProps {
  teamMembers: Array<{ id: string; userId: string; name: string; displayName?: string }>;
}

const LEAVE_TYPES = ['sick', 'paid', 'casual', 'bereavement', 'maternity', 'paternity', 'compensatory', 'unpaid', 'other'];

const emptyForm = {
  userGymMappingId: '',
  leaveType: '',
  startDate: '',
  endDate: '',
  isHalfDay: false,
  halfDayType: '',
  reason: ''
};

export default function TeamLeavesTab({ teamMembers }: TeamLeavesTabProps) {
  const [leaves, setLeaves] = useState<LeaveRecord[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<Array<{ value: string; label: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState<LeaveRecord | null>(null);
  const [filters, setFilters] = useState({
    teamMemberId: '',
    showPastLeaves: false
  });

  const [formData, setFormData] = useState(emptyForm);
  const [editData, setEditData] = useState(emptyForm);

  const getMinLeaveDate = () => {
    const now = new Date();
    const minDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    return minDate.toISOString().split('T')[0];
  };

  const getMaxLeaveDate = () => {
    const now = new Date();
    const maxDate = new Date(now.getFullYear(), now.getMonth() + 2, now.getDate());
    return maxDate.toISOString().split('T')[0];
  };

  const getAuthHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('No access token');
    return {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchLeaves = async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      const queryParams = new URLSearchParams();
      if (filters.teamMemberId) queryParams.append('teamMemberId', filters.teamMemberId);

      if (filters.showPastLeaves) {
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        queryParams.append('startDate', sixMonthsAgo.toISOString().split('T')[0]);
      } else {
        queryParams.append('startDate', new Date().toISOString().split('T')[0]);
      }
      queryParams.append('sort', 'asc');

      const response = await fetch(`${API_BASE_URL}/api/leaves?${queryParams}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setLeaves(data.data || []);
      } else {
        throw new Error('Failed to fetch leaves');
      }
    } catch (error) {
      console.error('Error fetching leaves:', error);
      toast.error('Failed to load leave records');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveTypes = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/leaves/types`, { headers });
      if (response.ok) {
        const data = await response.json();
        setLeaveTypes(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching leave types:', error);
    }
  };

  useEffect(() => {
    fetchLeaves();
    fetchLeaveTypes();
  }, []);

  const handleCreateLeave = async () => {
    try {
      if (!formData.userGymMappingId) { toast.error('Please select a team member'); return; }
      if (!formData.leaveType) { toast.error('Please select a leave type'); return; }
      if (!formData.startDate) { toast.error('Start date is required'); return; }

      const minDate = new Date(getMinLeaveDate());
      const maxDate = new Date(getMaxLeaveDate());
      const startDate = new Date(formData.startDate);
      const endDate = formData.isHalfDay ? startDate : new Date(formData.endDate);

      if (startDate < minDate) { toast.error('Leave can only be added up to 1 month in the past'); return; }
      if (endDate > maxDate) { toast.error('Leave can only be added up to 2 months in the future'); return; }
      if (endDate < startDate) { toast.error('End date must be on or after start date'); return; }

      const headers = await getAuthHeaders();
      const payload: any = {
        userGymMappingId: formData.userGymMappingId,
        leaveType: formData.leaveType,
        startDate: formData.startDate,
        endDate: formData.isHalfDay ? formData.startDate : formData.endDate,
        isHalfDay: formData.isHalfDay,
        reason: formData.reason || undefined
      };
      if (formData.isHalfDay && formData.halfDayType) {
        payload.halfDayType = formData.halfDayType;
      }

      const response = await fetch(`${API_BASE_URL}/api/leaves`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create leave');
      }

      toast.success('Leave record created successfully');
      setShowCreateModal(false);
      setFormData(emptyForm);
      fetchLeaves();
    } catch (error: any) {
      console.error('Error creating leave:', error);
      toast.error(error.message || 'Failed to create leave');
    }
  };

  const handleEditLeave = async () => {
    if (!selectedLeave) return;
    try {
      const headers = await getAuthHeaders();
      const payload: any = {
        leaveType: editData.leaveType,
        startDate: editData.startDate,
        endDate: editData.isHalfDay ? editData.startDate : editData.endDate,
        isHalfDay: editData.isHalfDay,
        halfDayType: editData.isHalfDay ? editData.halfDayType || null : null,
        reason: editData.reason || null
      };

      const response = await fetch(`${API_BASE_URL}/api/leaves/${selectedLeave.id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update leave');
      }

      toast.success('Leave updated successfully');
      setShowEditModal(false);
      setSelectedLeave(null);
      fetchLeaves();
    } catch (error: any) {
      console.error('Error editing leave:', error);
      toast.error(error.message || 'Failed to update leave');
    }
  };

  const handleApproveReject = async (status: 'approved' | 'rejected', rejectionReason?: string) => {
    if (!selectedLeave) return;
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/leaves/${selectedLeave.id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          status,
          rejectionReason: status === 'rejected' ? rejectionReason : undefined
        })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${status} leave`);
      }

      toast.success(`Leave ${status} successfully`);
      setShowApproveModal(false);
      setSelectedLeave(null);
      fetchLeaves();
    } catch (error: any) {
      console.error(`Error ${status} leave:`, error);
      toast.error(error.message || `Failed to ${status} leave`);
    }
  };

  const handleDeleteLeave = async () => {
    if (!selectedLeave) return;
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/leaves/${selectedLeave.id}`, {
        method: 'DELETE',
        headers
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete leave');
      }

      toast.success('Leave deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedLeave(null);
      fetchLeaves();
    } catch (error: any) {
      console.error('Error deleting leave:', error);
      toast.error(error.message || 'Failed to delete leave');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const calculateDays = (start: string, end: string, isHalfDay: boolean) => {
    const days = Math.ceil((new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60 * 60 * 24)) + 1;
    return isHalfDay ? `0.5 days` : `${days} day${days !== 1 ? 's' : ''}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const openEditModal = (leave: LeaveRecord) => {
    setSelectedLeave(leave);
    setEditData({
      userGymMappingId: '',
      leaveType: leave.leaveType,
      startDate: leave.startDate.split('T')[0],
      endDate: leave.endDate.split('T')[0],
      isHalfDay: leave.isHalfDay,
      halfDayType: leave.halfDayType || '',
      reason: leave.reason || ''
    });
    setShowEditModal(true);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Team Member</label>
            <select
              value={filters.teamMemberId}
              onChange={(e) => setFilters(prev => ({ ...prev, teamMemberId: e.target.value }))}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">All Members</option>
              {teamMembers.map(member => (
                <option key={member.id} value={member.userId}>{member.displayName || member.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.showPastLeaves}
                onChange={(e) => setFilters(prev => ({ ...prev, showPastLeaves: e.target.checked }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
              />
              <span className="text-xs font-medium text-gray-700">Show all past leaves (past pending always shown)</span>
            </label>
          </div>
          <div className="md:col-span-2 flex items-center justify-end gap-2">
            <button
              onClick={fetchLeaves}
              className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Apply Filters
            </button>
            <button
              onClick={() => {
                setFilters({ teamMemberId: '', showPastLeaves: false });
                setTimeout(() => fetchLeaves(), 0);
              }}
              className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Add Leave</span>
            </button>
          </div>
        </div>
      </div>

      {/* Leaves List */}
      {leaves.length === 0 ? (
        <EmptyState
          title="No leave records"
          description="Leave records will appear here once they are created."
        />
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team Member</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Leave Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leaves.map((leave) => (
                  <tr key={leave.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{leave.teamMember.displayName || leave.teamMember.name}</div>
                        <div className="text-sm text-gray-500">{leave.gym?.displayName || ''}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 capitalize">{leave.leaveType.replace('_', ' ')}</span>
                      {leave.isHalfDay && (
                        <span className="ml-2 text-xs text-gray-500">({leave.halfDayType?.replace('_', ' ')})</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(leave.startDate)} – {formatDate(leave.endDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {calculateDays(leave.startDate, leave.endDate, leave.isHalfDay)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(leave.status)}`}>
                        {leave.status.charAt(0).toUpperCase() + leave.status.slice(1)}
                      </span>
                      {leave.status === 'approved' && leave.approvedBy && (
                        <div className="text-xs text-gray-400 mt-0.5">by {leave.approvedBy.name}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-3">
                        {leave.status === 'pending' && (
                          <>
                            <button
                              onClick={() => { setSelectedLeave(leave); setShowApproveModal(true); }}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Review
                            </button>
                            <button
                              onClick={() => openEditModal(leave)}
                              className="text-gray-600 hover:text-gray-900"
                            >
                              Edit
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => { setSelectedLeave(leave); setShowDeleteConfirm(true); }}
                          className="text-red-500 hover:text-red-700"
                          title="Delete leave"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Leave Modal */}
      {showCreateModal && (
        <LeaveFormModal
          title="Add Leave Record"
          formData={formData}
          setFormData={setFormData}
          leaveTypes={leaveTypes}
          teamMembers={teamMembers}
          showMemberSelect={true}
          getMinLeaveDate={getMinLeaveDate}
          getMaxLeaveDate={getMaxLeaveDate}
          onSubmit={handleCreateLeave}
          onClose={() => { setShowCreateModal(false); setFormData(emptyForm); }}
          submitLabel="Create Leave"
        />
      )}

      {/* Edit Leave Modal */}
      {showEditModal && selectedLeave && (
        <LeaveFormModal
          title="Edit Leave Record"
          formData={editData}
          setFormData={setEditData}
          leaveTypes={leaveTypes}
          teamMembers={teamMembers}
          showMemberSelect={false}
          getMinLeaveDate={getMinLeaveDate}
          getMaxLeaveDate={getMaxLeaveDate}
          onSubmit={handleEditLeave}
          onClose={() => { setShowEditModal(false); setSelectedLeave(null); }}
          submitLabel="Save Changes"
        />
      )}

      {/* Approve/Reject Modal */}
      {showApproveModal && selectedLeave && (
        <ApproveModal
          leave={selectedLeave}
          formatDate={formatDate}
          onApprove={() => handleApproveReject('approved')}
          onReject={(reason) => handleApproveReject('rejected', reason)}
          onClose={() => { setShowApproveModal(false); setSelectedLeave(null); }}
        />
      )}

      {/* Delete Confirm Modal */}
      {showDeleteConfirm && selectedLeave && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Leave</h3>
            <p className="text-sm text-gray-600 mb-1">
              Are you sure you want to delete this leave for <strong>{selectedLeave.teamMember.displayName || selectedLeave.teamMember.name}</strong>?
            </p>
            <p className="text-xs text-gray-400 mb-6">The record will be kept in the system but hidden from the list.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowDeleteConfirm(false); setSelectedLeave(null); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteLeave}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Sub-components ----

interface LeaveFormModalProps {
  title: string;
  formData: typeof emptyForm;
  setFormData: React.Dispatch<React.SetStateAction<typeof emptyForm>>;
  leaveTypes: Array<{ value: string; label: string }>;
  teamMembers: Array<{ id: string; userId: string; name: string; displayName?: string }>;
  showMemberSelect: boolean;
  getMinLeaveDate: () => string;
  getMaxLeaveDate: () => string;
  onSubmit: () => void;
  onClose: () => void;
  submitLabel: string;
}

function LeaveFormModal({
  title, formData, setFormData, leaveTypes, teamMembers, showMemberSelect,
  getMinLeaveDate, getMaxLeaveDate, onSubmit, onClose, submitLabel
}: LeaveFormModalProps) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            {showMemberSelect && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Team Member</label>
                <select
                  value={formData.userGymMappingId}
                  onChange={(e) => setFormData(prev => ({ ...prev, userGymMappingId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Team Member</option>
                  {teamMembers.map(member => (
                    <option key={member.id} value={member.id}>
                      {member.displayName || member.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Leave Type</label>
              <select
                value={formData.leaveType}
                onChange={(e) => setFormData(prev => ({ ...prev, leaveType: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select Leave Type</option>
                {(leaveTypes.length > 0 ? leaveTypes : LEAVE_TYPES.map(v => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1).replace(/_/g, ' ') }))).map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    startDate: e.target.value,
                    endDate: prev.isHalfDay ? e.target.value : prev.endDate
                  }))}
                  min={getMinLeaveDate()}
                  max={getMaxLeaveDate()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                  min={getMinLeaveDate()}
                  max={getMaxLeaveDate()}
                  disabled={formData.isHalfDay}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                  required={!formData.isHalfDay}
                />
              </div>
            </div>

            <div>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isHalfDay}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    isHalfDay: e.target.checked,
                    endDate: e.target.checked ? prev.startDate : prev.endDate
                  }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Half Day</span>
              </label>
            </div>

            {formData.isHalfDay && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Half Day Type</label>
                <select
                  value={formData.halfDayType}
                  onChange={(e) => setFormData(prev => ({ ...prev, halfDayType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select</option>
                  <option value="first_half">First Half</option>
                  <option value="second_half">Second Half</option>
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Reason (Optional)</label>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter reason for leave..."
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface ApproveModalProps {
  leave: LeaveRecord;
  formatDate: (d: string) => string;
  onApprove: () => void;
  onReject: (reason: string) => void;
  onClose: () => void;
}

function ApproveModal({ leave, formatDate, onApprove, onReject, onClose }: ApproveModalProps) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000] p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Review Leave Request</h3>
          <div className="space-y-3 mb-6">
            <div><span className="text-sm font-medium text-gray-700">Team Member:</span><span className="ml-2 text-sm text-gray-900">{leave.teamMember.displayName || leave.teamMember.name}</span></div>
            <div><span className="text-sm font-medium text-gray-700">Leave Type:</span><span className="ml-2 text-sm text-gray-900 capitalize">{leave.leaveType.replace('_', ' ')}</span></div>
            <div>
              <span className="text-sm font-medium text-gray-700">Period:</span>
              <span className="ml-2 text-sm text-gray-900">{formatDate(leave.startDate)} – {formatDate(leave.endDate)}</span>
            </div>
            {leave.reason && (
              <div><span className="text-sm font-medium text-gray-700">Reason:</span><p className="mt-1 text-sm text-gray-900">{leave.reason}</p></div>
            )}
          </div>

          {rejecting && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Rejection Reason</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="Enter reason for rejection..."
              />
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
              Cancel
            </button>
            {rejecting ? (
              <>
                <button onClick={() => setRejecting(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                  Back
                </button>
                <button
                  onClick={() => onReject(reason || 'Leave request rejected')}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Confirm Reject
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setRejecting(true)} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors">
                  Reject
                </button>
                <button onClick={onApprove} className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors">
                  Approve
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
